/**
 * captureDrawerScopes — the pure decisions behind the builder Capture pane's
 * two sections and its media-type filter row (QA-77).
 *
 * Gordon, verbatim (QA-77): "On the Wheel Builder, the Capture side pane needs
 * two sections (tabs or a radio dial): 1) Captures ASSIGNED to this Wheel,
 * 2) the full Capture Library (including the ones assigned to this Wheel). Each
 * option should display the total number of captures in that section. Also, if
 * multiple types of media are available in the Edit-Page Capture, display tabs
 * at the top to filter by media type."
 *
 * Every decision that sentence implies is extracted here — no React, no
 * queries — so each one can be asserted on its own and a failure names the
 * decision that broke instead of sending a reader into a component render log
 * (Nick's principle 3).
 *
 * The counting rule is inherited, not reinvented: `Capture.helpers`'
 * CHIP_COUNT_PARTIAL / formatChipCount exist because a page-scoped `0` once
 * told a user they owned no videos while they owned one (measured on staging
 * 2026-07-30, bundle main.8b745d1a.js). A section badge is under the identical
 * obligation, so an unverified total is reported as partial rather than as a
 * confident number.
 */

import { CAPTURE_TYPES, attachedWheelsList } from "../Capture.helpers";

/** The two sections of the builder Capture pane. */
export const DRAWER_SCOPE = {
  /** Only the captures attached to the wheel currently open in the builder. */
  WHEEL: "wheel",
  /** The owner's whole Capture library, INCLUDING the attached ones. */
  LIBRARY: "library",
};

/** Tab labels, in the order they render. */
export const DRAWER_SCOPE_LABEL = {
  [DRAWER_SCOPE.WHEEL]: "Assigned to Wheel",
  [DRAWER_SCOPE.LIBRARY]: "All Captures",
};

/** Render order — "assigned to this Wheel" is section 1 in Gordon's note. */
export const DRAWER_SCOPE_ORDER = [DRAWER_SCOPE.WHEEL, DRAWER_SCOPE.LIBRARY];

/**
 * The total for one section, and whether that total is actually KNOWN.
 *
 * DRF page responses carry `count` (the server's total across every page), so
 * when it is present the badge can state the real total even though only page 1
 * is in memory. When it is absent the only honest answer is "at least this
 * many", which `formatChipCount` renders as "N+" / "…".
 *
 * @param {{pages?: Array<{count?: number}>}} data react-query infinite data
 * @param {Array} loadedItems the flattened rows currently in memory
 * @returns {{value: number, complete: boolean}}
 */
export const scopeCount = (data, loadedItems = []) => {
  const loaded = Array.isArray(loadedItems) ? loadedItems.length : 0;
  const total = data?.pages?.[0]?.count;
  if (typeof total === "number" && Number.isFinite(total)) {
    return { value: total, complete: true };
  }
  return { value: loaded, complete: false };
};

/**
 * Which capture types are actually present in a list, in a stable order.
 *
 * Stable order matters: a filter row whose tabs reorder as rows load in is a
 * moving target for a pointer and for a screen reader's tab index alike.
 */
export const presentTypes = (items = []) => {
  const seen = new Set(
    (Array.isArray(items) ? items : []).map((item) => item && item.type).filter(Boolean)
  );
  return Object.values(CAPTURE_TYPES).filter((type) => seen.has(type));
};

/**
 * Should the media-type filter row render at all?
 *
 * Gordon's condition is "if multiple types of media are available" — one type
 * (or none) means every tab would select the same rows, so the row would be
 * pure chrome. Two or more distinct types is the trigger.
 */
export const shouldShowTypeTabs = (items = []) => presentTypes(items).length > 1;

/**
 * What the pane says when the active section has nothing to show.
 *
 * The empty state must name the section the user is ACTUALLY looking at. Two
 * wrong answers are possible without this and both are worse than useless:
 * telling someone "no captured items on this wheel yet" while they stand on the
 * Full Library tab, and telling someone their library is empty when a media-type
 * tab is the thing hiding the rows.
 *
 * QA-127 #3: `hasQuery` covers the new search box the same way — a search
 * that matches nothing is "no matching captures", not "this section/wheel
 * has nothing at all", which would send the user hunting for a bug that
 * isn't there.
 *
 * @param {{scope?: string, typeFilter?: string, typeLabel?: string, hasQuery?: boolean}} opts
 * @returns {{title: string, body: string}}
 */
export const drawerEmptyCopy = ({
  scope = DRAWER_SCOPE.WHEEL,
  typeFilter = "all",
  typeLabel = "",
  hasQuery = false,
} = {}) => {
  if (hasQuery) {
    return {
      title: "No matching captures",
      body: 'Nothing here matches your search. Try a different word, or clear the search box.',
    };
  }
  if (typeFilter && typeFilter !== "all") {
    return {
      title: "No matching captures",
      body: `Nothing in this section is ${(typeLabel || "that type").toLowerCase()}. Choose "All" to see everything here.`,
    };
  }
  if (scope === DRAWER_SCOPE.LIBRARY) {
    return {
      title: "Your Capture library is empty",
      body: "Captures you take — notes, photos, videos, audio, documents — collect here, ready to drop into any Segment.",
    };
  }
  return {
    title: "No captured items on this wheel yet",
    body: "Attach a note, photo, or document to this wheel from your Capture inbox, or switch to All Captures to pull one in.",
  };
};

/**
 * A capture belongs to the wheel currently open when any of its M2M links
 * (`attached_wheels` / `attached_wheel_ids` / legacy `attached_wheel`) match.
 *
 * Compared as strings because the wheel id arrives as a route param (a string)
 * on the builder side and as a number on the serialized capture — a `===` on
 * the raw values reports every attached row as unattached, which would strip
 * the "Attached" chip and the Detach action off rows that really are attached.
 */
export const isAttachedToWheel = (item, wheelId) => {
  if (!item || wheelId === null || wheelId === undefined || wheelId === "") return false;
  return attachedWheelsList(item).some((wheel) => String(wheel.id) === String(wheelId));
};
